import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Package, Trash2, ShieldCheck, Eye, EyeOff, CheckCircle,
  AlertTriangle, Clock, Database, UserX, ListChecks,
} from 'lucide-react';

// Data deleted immediately on account deletion
const DATA_DELETED = [
  { label: 'Compte et identifiants', detail: 'Nom d\'utilisateur, adresse e-mail, mot de passe (hash)' },
  { label: 'Articles et stocks', detail: 'Tous les articles, categories, seuils d\'alerte' },
  { label: 'Mouvements et achats', detail: 'Historique complet des achats, ventes, consommations' },
  { label: 'Notifications et rapports', detail: 'Alertes de stock, rapports periodiques' },
  { label: 'Foyer et partage', detail: 'Appartenance au foyer, invitations, membres lies' },
  { label: 'Retours et journaux', detail: 'Feedbacks, rapports de bugs, journaux d\'erreurs' },
  { label: 'Donnees de paiement', detail: 'Historique des transactions (les donnees de carte restent chez Stripe)' },
];

// Data retained after deletion (legal obligations)
const DATA_RETAINED = [
  {
    label: 'Donnees de facturation Stripe',
    detail: 'Conservees par Stripe (processeur de paiement tiers) conformement a ses obligations legales.',
    duration: 'Selon la politique de Stripe (jusqu\'a 7 ans)',
  },
  {
    label: 'Journaux de securite anonymises',
    detail: 'Adresse IP et horodatage des connexions, sans lien avec votre identite.',
    duration: '30 jours maximum',
  },
];

// Step-by-step procedure displayed before the form
const PROCEDURE_STEPS = [
  'Connectez-vous ci-dessous avec vos identifiants SmartInventory.',
  'Verifiez la liste des donnees qui seront supprimees.',
  'Confirmez la suppression definitive de votre compte.',
  'Vous recevrez une confirmation a l\'ecran. La suppression est immediate et irreversible.',
];

function DeleteAccountPage() {
  const [step, setStep] = useState('info'); // 'info' | 'form' | 'confirm' | 'done'
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post('/auth/login', formData);
      setToken(res.data.access_token);
      setStep('confirm');
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Identifiants incorrects. Verifiez votre nom d'utilisateur et votre mot de passe."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await axios.delete('/user/account', {
        headers: { Authorization: `Bearer ${token}` },
        data: { password: formData.password },
      });
      setStep('done');
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          'Erreur lors de la suppression. Veuillez reessayer.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-cyan-50 to-teal-100 p-4 py-10">
      <div className="mx-auto w-full max-w-2xl space-y-6">

        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-3">
            <div className="bg-gradient-to-br from-emerald-500 to-teal-600 p-3 rounded-2xl shadow-lg">
              <Package className="w-10 h-10 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-0.5">SmartInventory</h1>
          <p className="text-sm text-gray-500">Developpe par Mohamed Bouras</p>
          <p className="mt-2 text-base font-semibold text-red-700">Demande de suppression de compte</p>
        </div>

        {/* ── INFO PANEL (always visible) ── */}
        {step !== 'done' && (
          <>
            {/* Procedure */}
            <Card className="border border-slate-200 shadow-sm">
              <CardHeader className="pb-2 px-5 pt-5">
                <div className="flex items-center gap-2">
                  <ListChecks className="h-5 w-5 text-emerald-600 shrink-0" />
                  <CardTitle className="text-base font-semibold text-slate-800">
                    Procedure de suppression
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <ol className="space-y-2">
                  {PROCEDURE_STEPS.map((s, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">
                        {i + 1}
                      </span>
                      {s}
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>

            {/* Data deleted */}
            <Card className="border border-red-100 shadow-sm">
              <CardHeader className="pb-2 px-5 pt-5">
                <div className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-red-500 shrink-0" />
                  <CardTitle className="text-base font-semibold text-slate-800">
                    Donnees supprimees immediatement
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <ul className="space-y-2">
                  {DATA_DELETED.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <UserX className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
                      <span>
                        <span className="font-medium text-slate-800">{d.label} :</span>{' '}
                        <span className="text-slate-600">{d.detail}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Data retained */}
            <Card className="border border-amber-100 shadow-sm">
              <CardHeader className="pb-2 px-5 pt-5">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-500 shrink-0" />
                  <CardTitle className="text-base font-semibold text-slate-800">
                    Donnees conservees apres suppression
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <ul className="space-y-3">
                  {DATA_RETAINED.map((d, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Database className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
                      <span>
                        <span className="font-medium text-slate-800">{d.label} :</span>{' '}
                        <span className="text-slate-600">{d.detail}</span>{' '}
                        <span className="inline-block mt-0.5 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          {d.duration}
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </>
        )}

        {/* ── STEP: info CTA ── */}
        {step === 'info' && (
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => setStep('form')}
              className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-semibold"
            >
              Demander la suppression de mon compte
            </Button>
            <Link to="/login">
              <Button variant="outline" className="w-full h-11">
                Retour a la connexion
              </Button>
            </Link>
          </div>
        )}

        {/* ── STEP: form ── */}
        {step === 'form' && (
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2 px-6 pt-6">
              <div className="flex justify-center mb-2">
                <AlertTriangle className="h-7 w-7 text-red-500" />
              </div>
              <CardTitle className="text-lg font-semibold text-gray-900">
                Connexion requise pour confirmer
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Entrez vos identifiants SmartInventory pour authentifier la demande.
              </p>
            </CardHeader>

            <CardContent className="px-6 pb-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="username">Nom d'utilisateur</Label>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    required
                    autoComplete="username"
                    value={formData.username}
                    onChange={handleChange}
                    className="h-11"
                    placeholder="Votre nom d'utilisateur"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      autoComplete="current-password"
                      value={formData.password}
                      onChange={handleChange}
                      className="h-11 pr-10"
                      placeholder="Votre mot de passe"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-semibold"
                >
                  {loading ? 'Verification...' : 'Continuer vers la confirmation'}
                </Button>
              </form>

              <Button
                variant="outline"
                onClick={() => setStep('info')}
                className="w-full h-11 mt-3"
              >
                Retour
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── STEP: confirm ── */}
        {step === 'confirm' && (
          <Card className="shadow-xl border-0">
            <CardHeader className="text-center pb-2 px-6 pt-6">
              <div className="flex justify-center mb-2">
                <Trash2 className="h-7 w-7 text-red-500" />
              </div>
              <CardTitle className="text-lg font-semibold text-gray-900">
                Derniere confirmation
              </CardTitle>
              <p className="text-sm text-gray-500 mt-1">
                Connecte en tant que <strong>{formData.username}</strong>. Cette action est <strong>irreversible</strong>.
              </p>
            </CardHeader>

            <CardContent className="px-6 pb-6 space-y-4">
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start gap-2 text-sm text-red-800">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>
                  Toutes vos donnees listees ci-dessus seront <strong>definitivement supprimees</strong> immediatement apres confirmation.
                </span>
              </div>

              <Button
                onClick={handleDelete}
                disabled={loading}
                className="w-full h-11 bg-red-600 hover:bg-red-700 text-white font-semibold"
              >
                {loading ? 'Suppression en cours...' : 'Oui, supprimer definitivement mon compte'}
              </Button>

              <Button
                variant="outline"
                onClick={() => setStep('form')}
                disabled={loading}
                className="w-full h-11"
              >
                Annuler
              </Button>
            </CardContent>
          </Card>
        )}

        {/* ── STEP: done ── */}
        {step === 'done' && (
          <Card className="shadow-2xl border-0">
            <CardContent className="px-6 py-12 text-center space-y-4">
              <div className="flex justify-center">
                <CheckCircle className="h-14 w-14 text-emerald-500" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Compte supprime</h2>
              <p className="text-sm text-gray-600 max-w-sm mx-auto">
                Votre compte et l'ensemble de vos donnees ont ete definitivement supprimes de SmartInventory. Merci de nous avoir fait confiance.
              </p>
              <div className="flex justify-center items-center gap-1.5 pt-2">
                <ShieldCheck className="h-5 w-5 text-emerald-500" />
                <span className="text-xs text-emerald-700 font-medium">
                  Suppression conforme au RGPD / Google Play Data Safety
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        {step !== 'done' && (
          <p className="text-center text-xs text-gray-400">
            SmartInventory &mdash; Mohamed Bouras &bull;{' '}
            <Link to="/privacy-policy" className="underline hover:text-emerald-600">
              Politique de confidentialite
            </Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default DeleteAccountPage;
